import { useParams } from 'react-router-dom';

import { graphql } from '@e-socna/data-layer';

import { Tabs, TabsStrategy } from '../../components/Tabs';
import {
  PATH_MISSION_ADDRESS,
  PATH_MISSION_ADDRESS_EDIT,
  PATH_MISSION_ARCHIVE_INFO,
  PATH_MISSION_EXTERNAL_ACTORS,
  PATH_MISSION_EXTERNAL_ACTORS_EDIT,
  PATH_MISSION_EXTERNAL_ACTORS_EDIT_CUSTOMIZE,
  PATH_MISSION_EXTERNAL_ACTORS_EDIT_DELETE,
  PATH_MISSION_EXTERNAL_ACTORS_EDIT_FLUSH,
  PATH_MISSION_EXTERNAL_ACTORS_EDIT_MODIFY,
  PATH_MISSION_EXTERNAL_ACTORS_EDIT_NEW,
  PATH_MISSION_GENERAL,
  PATH_MISSION_GENERAL_EDIT,
  PATH_MISSION_GENERAL_EDIT_CODE,
  PATH_MISSION_GENERAL_EDIT_COLLABORATION,
  PATH_MISSION_GENERAL_EDIT_STATE,
  PATH_MISSION_INTERNAL_ACTORS,
  PATH_MISSION_INTERNAL_ACTORS_EDIT,
  PATH_MISSION_INTERNAL_ACTORS_EDIT_CUSTOMIZE,
  PATH_MISSION_INTERNAL_ACTORS_EDIT_DELETE,
  PATH_MISSION_INTERNAL_ACTORS_EDIT_FLUSH,
  PATH_MISSION_INTERNAL_ACTORS_EDIT_MODIFY,
  PATH_MISSION_INTERNAL_ACTORS_EDIT_NEW,
  PATH_MISSION_VERSIONS,
  PATH_MISSION_DEFAULT,
} from '../../app/constants/paths.constant';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';

import { Address } from './Address';
import { Actors } from './Actors';
import { ArchiveInfo } from './ArchiveInfo';
import { GeneralInfo } from './GeneralInfo';
import { Versions } from './Versions';

export const MissionPage_Tabs_Mission_Fragment = graphql(`
  fragment MissionPage_Tabs_Mission_Fragment on Mission {
    _id
    ...MissionPage_Address_Mission_Fragment
    ...MissionPage_Actors_Mission_Fragment
    ...MissionPage_ArchiveInfo_Mission_Fragment
    ...MissionPage_GeneralInfo_Mission_Fragment
    ...MissionPage_ServiceUsages_Mission_Fragment
  }
`);

export const MissionTabs = () => {
  const { folderId, missionId } = useMissionReadyContext();
  const { actorId } = useParams();

  const { paths, getActiveTab } = useBuildPaths(
    {
      address: PATH_MISSION_ADDRESS,
      addressEdit: PATH_MISSION_ADDRESS_EDIT,
      archiveInfo: PATH_MISSION_ARCHIVE_INFO,
      default: PATH_MISSION_DEFAULT,
      externalActors: PATH_MISSION_EXTERNAL_ACTORS,
      externalActorsEdit: PATH_MISSION_EXTERNAL_ACTORS_EDIT,
      externalActorsEditDelete: PATH_MISSION_EXTERNAL_ACTORS_EDIT_DELETE,
      externalActorsEditFlush: PATH_MISSION_EXTERNAL_ACTORS_EDIT_FLUSH,
      externalActorsEditCustomize: PATH_MISSION_EXTERNAL_ACTORS_EDIT_CUSTOMIZE,
      externalActorsEditModify: PATH_MISSION_EXTERNAL_ACTORS_EDIT_MODIFY,
      externalActorsEditNew: PATH_MISSION_EXTERNAL_ACTORS_EDIT_NEW,
      generalInfo: PATH_MISSION_GENERAL,
      generalInfoEdit: PATH_MISSION_GENERAL_EDIT,
      generalInfoEditCode: PATH_MISSION_GENERAL_EDIT_CODE,
      generalInfoEditCollaboration: PATH_MISSION_GENERAL_EDIT_COLLABORATION,
      generalInfoEditState: PATH_MISSION_GENERAL_EDIT_STATE,
      internalActors: PATH_MISSION_INTERNAL_ACTORS,
      internalActorsEdit: PATH_MISSION_INTERNAL_ACTORS_EDIT,
      internalActorsEditDelete: PATH_MISSION_INTERNAL_ACTORS_EDIT_DELETE,
      internalActorsEditFlush: PATH_MISSION_INTERNAL_ACTORS_EDIT_FLUSH,
      internalActorsEditCustomize: PATH_MISSION_INTERNAL_ACTORS_EDIT_CUSTOMIZE,
      internalActorsEditModify: PATH_MISSION_INTERNAL_ACTORS_EDIT_MODIFY,
      internalActorsEditNew: PATH_MISSION_INTERNAL_ACTORS_EDIT_NEW,
      versions: PATH_MISSION_VERSIONS,
    },
    {
      folderId,
      missionId,
      actorId,
    },
    {
      address: 'address',
      addressEdit: 'address',
      archiveInfo: 'archiveInfo',
      default: 'generalInfo',
      externalActors: 'externalActors',
      externalActorsEdit: 'externalActors',
      externalActorsEditDelete: 'externalActors',
      externalActorsEditFlush: 'externalActors',
      externalActorsEditCustomize: 'externalActors',
      externalActorsEditModify: 'externalActors',
      externalActorsEditNew: 'externalActors',
      generalInfo: 'generalInfo',
      generalInfoEdit: 'generalInfo',
      generalInfoEditCode: 'generalInfo',
      generalInfoEditCollaboration: 'generalInfo',
      generalInfoEditState: 'generalInfo',
      internalActors: 'internalActors',
      internalActorsEdit: 'internalActors',
      internalActorsEditDelete: 'internalActors',
      internalActorsEditFlush: 'internalActors',
      internalActorsEditCustomize: 'internalActors',
      internalActorsEditModify: 'internalActors',
      internalActorsEditNew: 'internalActors',
      versions: 'versions',
    },
  );

  const generalInfoPaths = {
    consult: paths.generalInfo,
    editGeneralInfo: paths.generalInfoEdit,
    editCode: paths.generalInfoEditCode,
    editCollaboration: paths.generalInfoEditCollaboration,
    editState: paths.generalInfoEditState,
  } as const;

  const addressPaths = {
    consult: paths.address,
    edit: paths.addressEdit,
  } as const;

  const internalActorsPaths = {
    consult: paths.internalActors,
    edit: paths.internalActorsEdit,
    editDelete: paths.internalActorsEditDelete,
    editFlush: paths.internalActorsEditFlush,
    editCustomize: paths.internalActorsEditCustomize,
    editModify: paths.internalActorsEditModify,
    editNew: paths.internalActorsEditNew,
  } as const;

  const externalActorsPaths = {
    consult: paths.externalActors,
    edit: paths.externalActorsEdit,
    editDelete: paths.externalActorsEditDelete,
    editFlush: paths.externalActorsEditFlush,
    editCustomize: paths.externalActorsEditCustomize,
    editModify: paths.externalActorsEditModify,
    editNew: paths.externalActorsEditNew,
  } as const;

  return (
    <Tabs
      activeTab={getActiveTab()}
      id="mission-tabs"
      strategy={TabsStrategy.Controlled}
      tabs={[
        {
          key: 'generalInfo',
          label: 'Informations Générales',
          panel: <GeneralInfo paths={generalInfoPaths} />,
          to: paths.generalInfo,
          value: 'generalInfo',
        },
        {
          key: 'address',
          label: 'Adresse',
          panel: <Address paths={addressPaths} />,
          to: paths.address,
          value: 'address',
        },
        {
          key: 'internalActors',
          label: 'Acteurs SOCNA',
          panel: <Actors isInternal paths={internalActorsPaths} />,
          to: paths.internalActors,
          value: 'internalActors',
        },
        {
          key: 'externalActors',
          label: 'Acteurs Externes',
          panel: <Actors isInternal={false} paths={externalActorsPaths} />,
          to: paths.externalActors,
          value: 'externalActors',
        },
        {
          key: 'archiveInfo',
          label: 'Archivage',
          panel: <ArchiveInfo />,
          to: paths.archiveInfo,
          value: 'archiveInfo',
        },
        {
          key: 'versions',
          label: 'Versions',
          panel: <Versions />,
          to: paths.versions,
          value: 'versions',
        },
      ]}
      title="Onglets de l'écran de Fiche d'une Mission"
    />
  );
};
